<template>
  <div
    class="c-mini-cart"
    :class="[
      'l-flex',
      'l-flex--column',
      'l-flex__nowrap'
    ]"
    role="dialog"
    :aria-describedby="ariaDialogTargetId"
  >
    <header class="c-mini-cart__header">
      <button
        class="c-mini-cart__close-button"
        @click="$emit('close')"
      >
        &larr;
      </button>
      <button
        :id="ariaDialogTargetId"
        ref="modalTarget"
        class="u-sr-only"
      >
        mini cart
      </button>
    </header>
    <div
      v-if="mappedProduct"
      class="c-mini-cart__wrapper"
      :class="[
        'l-flex',
        'l-flex--column',
        'l-flex--justify-start'
      ]"
    >
      <div class="u-margin-bottom-large" />
      <h2
        v-if="showCollectionTitle"
        class="c-mini-cart__collection-title t-heading-10 t-heading-10--x-light u-margin-bottom-xx-small"
      >
        Collection: {{ collectionTitle }}
      </h2>
      <h2 class="c-mini-cart__product-title t-heading-5 u-margin-bottom-small">
        {{ productTitle }}
      </h2>
      <mini-cart-media
        :product="mappedProduct"
      />
      <div
        v-if="productTypes.length > 1"
        class="c-mini-cart__select"
      >
        <!-- select collection -->
        <dropdown
          :value="activeProductType"
          :options="productTypes"
          :placeholder="activeProductType"
          @input="selectProductType"
        />
      </div>
      <div
        class="c-mini-cart__select"
        :class="[
          'l-flex',
          'l-flex__nowrap'
        ]"
      >
        <!-- select color -->
        <color-picker
          class="c-mini-cart__color-picker"
          v-bind="colorPickerProps"
          @select="selectColor"
        />
        <!-- select size -->
        <size-picker-dropdown
          class="c-mini-cart__size-picker"
          :options="sizeOptions"
          :active-option="activeSize"
          @select="selectSize"
        />
      </div>
      <div
        v-if="showWidthOptions"
        class="c-mini-cart__width-picker"
        :class="[
          'l-flex',
          'l-flex--align-end'
        ]"
      >
        <ring-width
          v-for="{ width, handle } in widthOptions"
          :key="`${handle}-${width}`"
          class="c-mini-cart__width"
          :width="width"
          :active="activeWidth"
          @select="selectWidth({ width, handle })"
        />
      </div>
      <slot
        v-if="showAddToCart"
        class="c-mini-cart__add-to-cart-button"
        :product="addToCart.product"
        :variant="addToCart.variant"
        :metafields="addToCart.metafields"
        :confirmed-selection="addToCart.confirmedSelection"
        :all-options-selected="addToCart.allOptionsSelected"
        :only-one-option="addToCart.onlyOneOption"
        :line-item-properties="lineItemProps"
        :show-price="false"
        :hide-price="true"
        custom-label="Add Free Gift"
      />
      <btn
        v-else
        @click="updateCart"
      >
        Update Cart
      </btn>
    </div>
    <div
      v-else
      class="c-mini-cart__wrapper"
    >
      Loading...
    </div>
  </div>
</template>

<script>
import Btn from '@/components/button/Btn'
import ColorPicker from '@/components/colorPicker/ColorPicker'
import get from 'lodash/get'
import MiniCartMedia from './MiniCartMedia'
import RingWidth from '@/components/ringWidth/RingWidth'
import SizePickerDropdown from '@/components/sizePicker/SizePickerDropdown'
import Dropdown from "../dropdown/Dropdown";
/**
 * TODO stack builder
 * @param {object} product
 * @param {object} productCollection
 * @param {{key: String, value: String}[]} lineItemProps
 * @fires MiniCart#update:product
 @ @fired MiniCart#remove:product
 * @fires MiniCart#input
 */
export default {
  components: {
    Btn,
    ColorPicker,
    MiniCartMedia,
    RingWidth,
    SizePickerDropdown,
    Dropdown
  },
  props: {
    product: {
      type: Object,
      required: true
    },
    productCollection: {
      type: Object,
      required: true
    },
    lineItems: {
      type: Array,
      default: () => []
    },
    lineItemProps: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedProduct: null,
      activeSize: '7',
      activeProductType: this.productTypes ? this.productTypes[0] : null,
    }
  },
  computed: {
    ariaDialogTargetId() {
      return `aria-modal-target-${this._uid}`
    },
    productTitle() {
      return get(this.selectedProduct, 'metafieldsMap.color.value.name')
    },
    collectionTitle() {
      return get(this.productCollection, 'collection.title')
    },
    showCollectionTitle() {
      const handle = get(this.productCollection, 'collection.handle')
      const title = this.collectionTitle
      return !(handle === title)
    },
    mappedProduct() {
      const product = this.product
      const selectedProduct = this.selectedProduct
      const productsByHandle = this.productsByHandle
      const handle = get(selectedProduct || product, 'handle')
      const id = get(selectedProduct || product, 'id')
      return {
        ...productsByHandle[handle],
        id
      }
    },
    selectedVariant() {
      const activeSize = this.activeSize
      const mappedProduct = this.mappedProduct
      const selectedProduct = this.selectedProduct
      const product = selectedProduct || mappedProduct
      return this.getVariant(activeSize, product)
    },
    showAddToCart() {
      const product = this.mappedProduct
      const isLineItem = this.isLineItem(product)
      const props = this.lineItemProps.map(({ key }) => key)
      const lineItems = this.lineItems
      let hasProps = false
      for (let item of lineItems) {
        if (item.customAttributes) {
          for (let { key } of item.customAttributes) {
            hasProps = props.includes(key)
            if (hasProps) {
              break
            }
          }
        }
        if (hasProps) {
          break
        }
      }
      // console.log({ result: (!hasProps || !isLineItem), hasProps, isLineItem })
      return (!hasProps && !isLineItem)
    },
    addToCart() {
      const mappedProduct = this.mappedProduct
      const selectedVariant = this.selectedVariant
      return {
        product: mappedProduct,
        variant: selectedVariant,
        metafields: (mappedProduct) ? mappedProduct.metafields : [],
        confirmedSelection: true,
        allOptionsSelected: true,
        onlyOneOption: true
      }
    },
    colorPickerProps() {
      return {
        product: this.mappedProduct,
        products: this.productCollection.products,
        renderLink: false
      }
    },
    filterByProductType(){
      const activeProductType = this.activeProductType
      const products = get(this.productCollection, 'products')
      let result = []
      if (products) {
        for (let product of products) {
          let collectionHandle = get(product, 'metafieldsMap.pdp_collection_handle.value', '');
          //jgunther: example: collectionHandle = 'pdp-collection-pyramid-stackable-silicone-rings' activeProductType = 'Pyramid Stackable Silicone Ring'
          //convert activeProductType to lowercase dashes and check if the collectionHandle contains that value and add it to the gwp select results
          if (collectionHandle.includes(activeProductType.toLowerCase().split(' ').join('-'))) {
            result.push(product)
          }
        }
      }
      return result
    },
    showWidthOptions() {
      const widthOptions = this.widthOptions
      return widthOptions && (widthOptions.length > 1)
    },
    activeWidth() {
      const product = this.mappedProduct
      return get(product, 'metafieldsMap.width.value')
    },
    widthOptions() {
      const activeProduct = this.mappedProduct
      const products = get(this.productCollection, 'products')
      const activeColor = get(activeProduct, 'metafieldsMap.color.value.name')
      const widthOrder = [
        'Wide',
        'Standard',
        'Thin',
        'Halo',
        'Stackable'
      ]
      let widths = {}
      let result = []
      if (products && products.length) {
        for (let product of products) {
          const width = get(product, 'metafieldsMap.width.value')
          const color = get(product, 'metafieldsMap.color.value.name')
          if (width && color && (color === activeColor)) {
            widths[width] = {
              width,
              handle: product.handle
            }
          }
        }
      }
      for (let width of widthOrder) {
        if (widths[width]) {
          result.push(widths[width])
        }
      }
      return result
    },
    sizeOptions() {
      const product = this.mappedProduct
      let result = []
      if (product) {
        for (let {name, value} of product.facets) {
          if (name.toLowerCase() === 'size') {
            result.push(value)
          }
        }
      }
      return result
    },
    productsByHandle() {
      const products = get(this.productCollection, 'products')
      let index = {}
      for (let product of (products || [])) {
        index[product.handle] = product
      }
      return index
    },
    productTypes(){
      const products = get(this.productCollection, 'products')
      let productTypes = []
      if (products) {
        for (let product of products) {
          let productType = product.title.split('-')[0].trim()
          if (!productTypes.includes(productType)) {
            productTypes.push(productType)
          }
        }
      }
      return productTypes
    }
  },
  watch: {
    activeProductType(){
      this.selectedProduct = this.filterByProductType[0]
    },
    selectedProduct(value, oldValue) {
      if (
        value &&
        oldValue &&
        oldValue.handle &&
        oldValue.handle !== value.handle
      ) {
        this.$emit('remove:product', {id: value.id, handle: value.handle})
      }
    },
    sizeOptions(sizes) {
      if (sizes && sizes.length && !sizes.includes(this.activeSize)) {
        this.activeSize = sizes[0]
      }
    },
    productCollection() {
      this.setInitialProduct()
    },
  },
  created() {
    this.activeProductType = this.productTypes[0]
    this.setInitialProduct()
  },
  async mounted() {
    try {
      await this.$nextTick()
      this.$refs.modalTarget.focus()
    } catch (err) {
      console.warn(err)
    }
  },
  methods: {
    isLineItem(product) {
      return (
        product &&
        (product.id.split('::').length === 2)
      )
    },
    updateProduct(product) {
      const initialProduct = this.product
      let id = product.id
      if (this.isLineItem(initialProduct)) {
        // if this is a line-item id, keep it
        id = initialProduct.id
      }
      this.selectedProduct = { ...product, id }
    },
    updateCart() {
      const { product, metafields } = this.addToCart
      const variant = this.getVariant(this.activeSize, this.mappedProduct)
      const customAttributes = this.lineItemProps
      const initialProduct = this.product
      if (product && variant) {
        this.$emit('update:product', {
          selectedProduct: {
            ...product,
            quantity: 1,
            customAttributes,
            metafields,
            variant
          },
          initialProductId: initialProduct.id
        })
      }
    },
    selectSize(size) {
      this.activeSize = size
    },
    selectWidth({ handle }) {
      this.updateProduct(
        this.productsByHandle[handle]
      )
    },
    selectColor(handle) {
      //console.log('selectColor', handle)
      this.updateProduct(
        this.productsByHandle[handle]
      )
    },
    selectProductType(value){
      this.activeProductType = value
    },
    setInitialProduct() {
      const product = this.product
      const collection = this.productCollection
      if (product && !this.selectedProduct) {
        if (product.variant) {
          for (let { name, value } of product.variant.selectedOptions) {
            if (name.toLowerCase() === 'size') {
              this.activeSize = value
              break
            }
          }
        }
        this.updateProduct(product)
      } else if (
        collection &&
        collection.products &&
        !this.selectedProduct
      ) {
        const product = get(collection, 'products[0]')
        this.selectedProduct = (product) ?  this.updateProduct(product) : null
      }
    },
    getVariant(activeSize, product) {
      let result = null
      for (let variant of (product.variants || [])) {
        const options = get(variant, 'selectedOptions') || []
        for (let option of options) {
          if (
            (option.name.toLowerCase() === 'size') &&
            (option.value == activeSize)
          ) {
            result = variant
          }
        }
        if (result) {
          break
        }
      }
      // console.log(activeSize, product, result)
      return result
    }
  }
}
</script>

<style lang="scss" scoped>
.c-mini-cart {
  width: 100%;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: rem(10px);
  @include themify($themes) {
    background-color: themed('foreground', 'white-text');
  }
  &__header {
    position: relative;
    width: 100%;
  }
  &__close-button {
    background: none;
    border: none;
    position: absolute;
    right: rem(10px);
    top: 0;
    bottom: 0;
    left: rem(10px);
    margin-top: auto;
    margin-bottom: auto;
    font-size: rem(30px);
    cursor: pointer;
  }
  &__wrapper {
    flex: 1;
    width: 100%;
    flex-wrap: nowrap;
    padding-bottom: rem(10px);
  }
  &__media {
    height: rem(400px);
    width: 100%;
  }
  &__size-picker {
    margin-left: rem(10px);
  }
  &__width + &__width {
    margin-left: rem(10px);
  }
  &__select {
    width: 100%;
    padding: rem(20px);
  }
}
</style>
